// ============================================================
// ChordSymbol
// ============================================================
// Renders a single chord as a typeset symbol.
//
// Handles:
//   - Accidental prefix (b/# rendered smaller and raised)
//   - Degree / note name
//   - Quality suffix
//   - Extensions
//   - Slash bass note
//   - Empty bar placeholder
// ============================================================

import React from 'react';
import type { Chord, DisplayMode, Key, AnalysisSettings } from '../../core/types';
import { ChordAnalysisLabel } from './ChordAnalysisLabel';
import {
  formatNashville,
  formatRoman,
  formatChordName,
} from '../../core/formatter';

// ------------------------------------------------------------
// PROPS
// ------------------------------------------------------------

interface ChordSymbolProps {
  chord: Chord;
  mode: DisplayMode;
  sectionKey: Key;
  /** If true, renders at a smaller size (e.g. in dense bars) */
  compact?: boolean;
  /** Override font size in px — used by SlashBar for dynamic scaling */
  overrideFontSize?: number;
  /** Analysis settings — if provided, shows analysis label below chord */
  analysisSettings?: AnalysisSettings;
}

// ------------------------------------------------------------
// PARSED DISPLAY PARTS
// We split the formatted string into semantic parts so we can
// apply fine typographic treatment to each piece.
// ------------------------------------------------------------

interface ChordParts {
  accidental: string;   // 'b' | '#' | ''
  root: string;         // '1' | 'IV' | 'F#'  (the core symbol)
  quality: string;      // 'm' | 'maj7' | '7' etc.
  extensions: string;   // 'add9' | '#11' etc.
  slash: string;        // '/3' | '/E' etc.
}

/**
 * Splits a formatted chord string into typographic parts.
 * This is a display-only split — not a full re-parse.
 *
 * We use the formatter output and split it at known boundaries
 * rather than re-running the parser.
 */
function splitChordParts(formatted: string): ChordParts {
  // Split off slash bass first
  const slashIdx = formatted.indexOf('/');
  const base = slashIdx >= 0 ? formatted.slice(0, slashIdx) : formatted;
  const slash = slashIdx >= 0 ? formatted.slice(slashIdx) : '';

  // Split leading accidental from the base
  let accidental = '';
  let rest = base;
  if (base.startsWith('b') || base.startsWith('#')) {
    accidental = base[0];
    rest = base.slice(1);
  }

  // For Nashville: root is the digit, rest is quality+ext
  // For Roman: root is the numeral (I, II... vii), rest is suffix
  // For chord names: root is note name (C, F#, Bb), rest is suffix

  // Extract the root token
  // Nashville: leading digit(s)
  const nashvilleMatch = rest.match(/^([1-7])(.*)/);
  // Roman: leading numeral
  const romanMatch = rest.match(/^(VII|III|IV|VI|II|VII|vii|iii|iv|vi|ii|V|v|I|i)(.*)/i);
  // Chord name: leading note name
  const chordMatch = rest.match(/^([A-G][b#]?)(.*)/);

  let root = rest;
  let qualityAndExt = '';

  if (nashvilleMatch) {
    root = nashvilleMatch[1];
    qualityAndExt = nashvilleMatch[2];
  } else if (romanMatch) {
    root = romanMatch[1];
    qualityAndExt = romanMatch[2];
  } else if (chordMatch) {
    root = chordMatch[1];
    qualityAndExt = chordMatch[2];
  }

  // Split quality from extensions (extensions start with 'add', 'b9', '#9' etc.)
  const extMatch = qualityAndExt.match(/(add\d+|[b#]\d+|\d{2,})(.*)/);
  const quality = extMatch ? qualityAndExt.slice(0, qualityAndExt.indexOf(extMatch[1])) : qualityAndExt;
  const extensions = extMatch ? extMatch[1] + extMatch[2] : '';

  return { accidental, root, quality, extensions, slash };
}

// ------------------------------------------------------------
// COMPONENT
// ------------------------------------------------------------

export function ChordSymbol({
  chord,
  mode,
  sectionKey,
  compact = false,
  overrideFontSize,
  analysisSettings,
}: ChordSymbolProps) {
  // Get the formatted string for this display mode
  const formatted =
    mode === 'nashville'
      ? formatNashville(chord)
      : mode === 'roman'
      ? formatRoman(chord)
      : formatChordName(chord, sectionKey);

  const parts = splitChordParts(formatted);

  // When overrideFontSize is set, use inline styles instead of Tailwind classes
  const useOverride = overrideFontSize !== undefined;
  const rootSize       = useOverride ? '' : compact ? 'text-2xl' : 'text-4xl';
  const suffixSize     = useOverride ? '' : compact ? 'text-sm'  : 'text-base';
  const accidentalSize = useOverride ? '' : compact ? 'text-base': 'text-xl';
  const rootStyle      = useOverride ? { fontSize: overrideFontSize } : undefined;
  const suffixStyle    = useOverride ? { fontSize: Math.max(10, overrideFontSize! * 0.45) } : undefined;
  const accStyle       = useOverride ? { fontSize: Math.max(9,  overrideFontSize! * 0.55) } : undefined;

  return (
    <span
      className="inline-flex items-baseline leading-none font-display select-none"
      aria-label={formatted}
    >
      {/* Accidental — smaller, raised */}
      {parts.accidental && (
        <span
          className={`${accidentalSize} relative -top-1 mr-0.5 text-ink-secondary font-display`}
          style={accStyle}
        >
          {parts.accidental === 'b' ? '♭' : '♯'}
        </span>
      )}

      {/* Root — the main symbol */}
      <span
        className={`${rootSize} font-display font-semibold text-ink-primary tracking-tight`}
        style={rootStyle}
      >
        {parts.root}
      </span>

      {/* Quality + extensions — smaller, baseline-aligned */}
      {(parts.quality || parts.extensions) && (
        <span
          className={`${suffixSize} font-display text-ink-primary ml-0.5 self-end mb-1`}
          style={suffixStyle}
        >
          {parts.quality}
          {parts.extensions && (
            <span className="text-ink-secondary">{parts.extensions}</span>
          )}
        </span>
      )}

      {/* Slash bass note */}
      {parts.slash && (
        <span
          className={`${suffixSize} text-ink-secondary font-display ml-0.5 self-end mb-1`}
          style={suffixStyle}
        >
          {parts.slash}
        </span>
      )}
    </span>
  );
}

// Re-export with analysis label wrapper
export function ChordSymbolWithAnalysis({
  chord,
  mode,
  sectionKey,
  compact,
  overrideFontSize,
  analysisSettings,
}: ChordSymbolProps) {
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <ChordSymbol
        chord={chord}
        mode={mode}
        sectionKey={sectionKey}
        compact={compact}
        overrideFontSize={overrideFontSize}
      />
      {analysisSettings?.enabled && (
        <ChordAnalysisLabel
          chord={chord}
          sectionKey={sectionKey}
          settings={analysisSettings}
          showBackground
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------
// EMPTY BAR PLACEHOLDER
// ------------------------------------------------------------

/**
 * Shown in empty bars that have no chords yet.
 */
export function EmptyBarPlaceholder() {
  return (
    <span className="text-ink-muted text-2xl font-display select-none opacity-30">
      —
    </span>
  );
}